import './map-reference-v2.css';

export type PriceTier = 1 | 2 | 3 | 4;

export type VerifiedReceiptPrice = {
  eligible_receipt_count: number;
  mean_amount: number | null;
  median_amount: number | null;
  currency: 'RUB';
  tier: PriceTier | null;
};

export const PRICE_TIER_LABELS: Record<PriceTier, string> = {
  1: '₽',
  2: '₽₽',
  3: '₽₽₽',
  4: '₽₽₽₽',
};

const PRICE_TIER_ARIA_LABELS: Record<PriceTier, string> = {
  1: 'До 1 000 ₽',
  2: 'От 1 000 до 2 000 ₽',
  3: 'От 2 000 до 3 500 ₽',
  4: 'Больше 3 500 ₽',
};

export function normalizeAverageCheckTiers(value: string | null): PriceTier[] {
  if (!value) return [];

  const tiers = value
    .split(',')
    .map((item) => Number(item.trim()))
    .filter((item): item is PriceTier => Number.isInteger(item) && item >= 1 && item <= 4);

  return [...new Set(tiers)].sort((left, right) => left - right);
}

export function AverageCheckFilter({
  tiers,
  onChange,
}: {
  tiers: PriceTier[];
  onChange: (tiers: PriceTier[]) => void;
}) {
  const active = new Set(tiers);

  return (
    <div className="map-average-check-filter">
      <div className="map-average-check-filter__chips" aria-label="Средний чек">
        {([1, 2, 3, 4] as PriceTier[]).map((tier) => {
          const selected = active.has(tier);
          return (
            <button
              className={selected ? 'chip map-average-check-filter__chip is-selected' : 'chip map-average-check-filter__chip'}
              key={tier}
              type="button"
              aria-label={PRICE_TIER_ARIA_LABELS[tier]}
              aria-pressed={selected}
              onClick={() => {
                const next = new Set(active);
                if (next.has(tier)) next.delete(tier);
                else next.add(tier);
                onChange([...next].sort((left, right) => left - right));
              }}
            >
              {PRICE_TIER_LABELS[tier]}
            </button>
          );
        })}
      </div>
      <span className="map-average-check-filter__note">По подтверждённым чекам</span>
    </div>
  );
}

export function verifiedReceiptPricePrimary(price: VerifiedReceiptPrice | null | undefined): string | null {
  if (!price || price.tier === null || price.median_amount === null) return null;

  const amount = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 }).format(price.median_amount);
  return `≈ ${amount} ₽`;
}

export function verifiedReceiptPriceSecondary(price: VerifiedReceiptPrice | null | undefined): string | null {
  if (!price || price.eligible_receipt_count <= 0) return null;
  return `по ${price.eligible_receipt_count} подтверждённым чекам`;
}

/**
 * Compatibility mount kept while the shared runtime still imports this symbol.
 * Map v2 owns avg_check directly inside App/React; there is deliberately no DOM
 * observer, fetch interception or visual bridge here.
 */
export function VerifiedAverageCheckBridge({ onRefresh: _onRefresh }: { onRefresh: () => void }) {
  return null;
}
